<template>
    <div>
        <v-card class="stock-item" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col class="d-flex align-center" cols="12" sm="9">
                        <v-btn dark icon @click="goBack">
                            <v-icon>fal fa-arrow-left</v-icon>
                        </v-btn>
                        <div class="ml-3" v-if="product">{{ $t('stock_item.heading', { value: product.name }) }}</div>
                    </v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn class="minw100" color="white" outlined @click="print">
                            <v-icon left>fal fa-print</v-icon>
                            {{ $t('general.print') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <stock-item-search
                    :filter.sync="filter"
                />

                <v-data-table
                    class="items-table striped"
                    :headers="headers"
                    :items="items"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="itemsTotal"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    dense
                >
                <template v-slot:foot="{ items }">
                    <tfoot>
                        <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>{{ $t('general.total').toUpperCase() }}:</td>
                            <td>{{ summary.inputQuantity.toFormat('0.000') }}</td>
                            <td>{{ summary.outputQuantity.toFormat('0.000') }}</td>
                            <td>{{ summary.quantity.toFormat('0.00') }}</td>
                            <td>{{ summary.sellingValue.toFormat('0.00') }}</td>
                            <td>{{ summary.sellingValueAfterTax.toFormat('0.00') }}</td>
                        </tr>
                    </tfoot>
                </template>
                    <template v-slot:item="{ item }">
                        <tr :class="getRowClass(item)">
                            <td>{{ item.date | date }}</td>
                            <td>{{ item.document }}</td>
                            <td>{{ item.number }}</td>
                            <td>{{ item.client ? item.client.name : '' }}</td>
                            <td>{{ Money.parse(item.selling_price).toFormat('0.00') }}</td>
                            <td>{{ Money.parse(item.selling_price_after_tax).toFormat('0.00') }}</td>
                            <td>{{ item.input_quantity !== null ? Money.parse(item.input_quantity).toFormat('0.000') : '' }}</td>
                            <td>{{ item.output_quantity !== null ? Money.parse(item.output_quantity).toFormat('0.000') : '' }}</td>
                            <td class="highlight">
                                <div>
                                    {{ item.quantity !== null ? Money.parse(item.quantity).toFormat('0.00') : '' }}
                                </div>
                            </td>
                            <td>{{ Money.parse(item.selling_value).toFormat('0.00') }}</td>
                            <td>{{ Money.parse(item.selling_value_after_tax).toFormat('0.00') }}</td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';
import StockItemSearch from '@/views/shared/admin/stock/StockItemSearch';
import HtmlGen from '@/wrappers/HtmlGen';

export default {
    components: {
        StockItemSearch,
    },

    async created() {
        const data = await loadData(`/admin/load/stock-item/${this.$route.params.id}`);
        this.product = new Product(data.product);
    },

    data() {
        return {
            items: [],
            itemsTotal: 0,
            loading: false,

            filter: {
                search: null,
                startDate: moment().subtract(1, 'month').format('YYYY-MM-DD') + ' 00:00:00',
                endDate: moment().format('YYYY-MM-DD') + ' 23:59:59',
            },

            headers: [
                { text: this.$t('stock_item.date'), value: 'date' },
                { text: this.$t('stock_item.document'), value: 'document', sortable: false },
                { text: this.$t('stock_item.number'), value: 'number', sortable: false },
                { text: this.$t('stock_item.client'), value: 'meta.client', sortable: false },
                { text: this.$t('stock_item.selling_price'), value: 'selling_price', sortable: false },
                { text: this.$t('stock_item.selling_price_after_tax'), value: 'selling_price_after_tax', sortable: false },
                { text: this.$t('stock_item.input_quantity'), value: 'input_quantity', sortable: false },
                { text: this.$t('stock_item.output_quantity'), value: 'output_quantity', sortable: false },
                { text: this.$t('stock_item.quantity'), value: 'quantity', sortable: false },
                { text: this.$t('stock_item.selling_value'), value: 'selling_value', sortable: false },
                { text: this.$t('stock_item.selling_value_after_tax'), value: 'selling_value_after_tax', sortable: false },
            ],

            pagination: {
                sortBy: ['date'],
                itemsPerPage: 100,
                page: 1,
                sortDesc: [true],
            },

            product: null,
        };
    },

    watch: {
        pagination: {
            handler() {
                this.getItems();
            },
            deep: true
        },

        filter() {
            this.getItems();
        },
    },

    computed: {
        summary() {
            return {
                inputQuantity: this.items.reduce((acc, cur) => acc.add(Money.parse(cur.input_quantity || 0)), Money.parse(0)),
                outputQuantity: this.items.reduce((acc, cur) => acc.add(Money.parse(cur.output_quantity || 0)), Money.parse(0)),
                quantity: this.items.reduce((acc, cur) => acc.add(Money.parse(cur.quantity || 0)), Money.parse(0)),
                sellingValue: this.items.reduce((acc, cur) => acc.add(Money.parse(cur.selling_value || 0)), Money.parse(0)),
                sellingValueAfterTax: this.items.reduce((acc, cur) => acc.add(Money.parse(cur.selling_value_after_tax || 0)), Money.parse(0)),
            }
        }
    },

    methods: {
        getItems() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            let url = `/admin/stock-item/${this.$route.params.id}?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;

            if (this.filter) {
                Object.keys(this.filter).forEach(key => {
                    if(this.filter[key]) {
                        url += `&${key}=${this.filter[key]}`;
                    }
                })
            }

            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.items = response.data.data.map(item => {
                    item.document = item.type === 'price_calculation'
                        ? this.$t('stock_item.price_calculation', { value: item.number, value2: item.client.name })
                        : item.type === 'price_leveling_auto'
                            ? this.$t('stock_item.price_leveling_auto_' + (item.meta.price_calculation ? 'calc' : 'receipt'), { value: item.meta.price_calculation?.number || item.meta.order?.number || '' })
                            : this.$t('stock_item.' + item.type, { value: item.number });

                    return item;
                });
                this.itemsTotal = response.data.total;
            });
        },

        getRowClass(item) {
            if(['beginning_inventory', 'price_calculation', 'refund_receipt', 'refund_credit_note'].includes(item.type)) {
                return 'success--text';
            }

            if(['write_off', 'sale_receipt'].includes(item.type)) {
                return 'error--text';
            }

            return 'primary--text';
        },

        print() {
            const lines = [];

            lines.push(`[HR]`);
            lines.push(`[C][H22]${this.$t('stock_item.heading', { value: this.product.name })}`);
            lines.push(`[HR]`);
            lines.push(`[L]`);
            lines.push(`[L]${this.tenant.venue.name}`);
            lines.push(`[L]${this.$t('general.tax_id_number')}: ${this.tenant.venue.tax_id_number}`);
            lines.push(`[L]${moment(this.filter.startDate).format('DD.MM.YYYY.')} - ${moment(this.filter.endDate).format('DD.MM.YYYY.')}`);

            lines.push(`[L]`);

            lines.push(`[HR]`);
            lines.push(
                `[L][H00]${this.$t('stock_item.date')}` +
                `[L]${this.$t('stock_item.document')}` +
                `[L]${this.$t('stock_item.number')}` +
                `[L]${this.$t('stock_item.client')}` +
                `[R]${this.$t('stock_item.selling_price')}` +
                `[R]${this.$t('stock_item.selling_price_after_tax')}` +
                `[R]${this.$t('stock_item.input_quantity')}` +
                `[R]${this.$t('stock_item.output_quantity')}` +
                `[R]${this.$t('stock_item.quantity')}` +
                `[R]${this.$t('stock_item.selling_value')}` +
                `[R]${this.$t('stock_item.selling_value_after_tax')}`
            );
            lines.push(`[HR]`);

            this.items.forEach((item, index) => {
                lines.push(
                    `[L][H00]${moment(item.date).format('DD.MM.YYYY.')}` +
                    `[L]${item.document}` +
                    `[L]${item.number}` +
                    `[L]${item.client?.name || ''}` +
                    `[R]${Money.parse(item.selling_price).toFormat('0.00')}` +
                    `[R]${Money.parse(item.selling_price_after_tax).toFormat('0.00')}` +
                    `[R]${item.input_quantity !== null ? Money.parse(item.input_quantity).toFormat('0.000') : ''}` +
                    `[R]${item.output_quantity !== null ? Money.parse(item.output_quantity).toFormat('0.000') : ''}` +
                    `[R]${item.quantity !== null ? Money.parse(item.quantity).toFormat('0.00') : ''}` +
                    `[R]${Money.parse(item.selling_value).toFormat('0.00')}` +
                    `[R]${Money.parse(item.selling_value_after_tax).toFormat('0.00')}`
                );
            })

            lines.push(`[HR]`);

            lines.push(
                `[L][H00][L][L][L][L]` +
                `[R]${this.$t('general.total').toUpperCase()}:` +
                `[R]${this.summary.inputQuantity.toFormat('0.000')}` +
                `[R]${this.summary.outputQuantity.toFormat('0.000')}` +
                `[R]${this.summary.quantity.toFormat('0.00')}` +
                `[R]${this.summary.sellingValue.toFormat('0.00')}` +
                `[R]${this.summary.sellingValueAfterTax.toFormat('0.00')}`
            );

            const html = lines.map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        },

        goBack() {
            this.$router.push({ name: 'adminStock' }).catch(err => {});
        },
    }
};
</script>

<style lang="scss" scoped>
.stock-item {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;

            &.highlight {
                background: rgb(247, 236, 127, 0.5);
            }
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }

        tfoot {
            font-weight: 500;
            background: #f3f4fb;
        }
    }
}
</style>
